import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { LoadingService } from '../../../shared/fi-loading/loading.service';
import { FiADIntegrationService } from '../ad-integration.service';
import { ObjectTypes } from '../../../shared/types/object-type';
import { Observable, Subject, Subscription } from 'rxjs';
import { FiSnackBarService } from '../../../shared/fi-shared-services';
import { AppTextService } from '../../../shared/app-text';
import {
    ADGroupMappingGroupCategory,
    IADGroupMapping,
    IADGroupMappingListModel,
    IADDisplayDataItem,
} from './group-mapping-definitions';

@Component({
    selector: 'fi-ad-external-platform-integration-group-mapping',
    templateUrl: './group-mapping.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./group-mapping.component.scss'],
    standalone: false,
})
export class FiADIntegrationGroupMappingComponent implements OnInit, OnDestroy {
    constructor(
        private loadingService: LoadingService,
        private adIntegrationService: FiADIntegrationService,
        @Inject('saveButtonAccessor') private saveButtonAccessor: { onSaveClick: Observable<void> },
        private snackBar: FiSnackBarService,
        private appTextService: AppTextService,
        @Inject('setDirtyCallback') private setDirtyCallback: (state?) => void,
    ) {}

    loadedData: IADGroupMappingListModel[] = [];
    displayData = [];
    changes: IADGroupMappingListModel[] = [];

    ngOnInit() {
        this.filter = this.sideOptions[0];
        this.saveButtonAccessor.onSaveClick.takeUntil(this.ngOnDestroy$).subscribe(() => this.saveGroupMappings());
        this.loadData();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    sideOptions = [
        {
            ObjectType: undefined,
            filter: false,
            TextId: 'ad_integration.all_groups',
        },
        {
            ObjectType: undefined,
            filter: true,
            TextId: 'ad_integration.not_mapped',
        },
        {
            ObjectType: ObjectTypes.Role,
            filter: true,
            TextId: 'common.roles',
        },
        {
            ObjectType: ObjectTypes.BusinessUnit,
            filter: true,
            TextId: 'common.units',
        },
    ];

    filter;

    SideListItemClick(option) {
        this.filter = option;
        this.updateDisplayData();
    }

    IsSideItemSelected(option) {
        return this.filter === option;
    }

    saveGroupMappings() {
        const saveData = this.loadedData.map((data) => ({
            Id: data.Id,
            GroupId: data.GroupId,
            ObjectType: data.ObjectType,
            ObjectId: data.ObjectId,
        }));

        this.loadingService.trackPromise(this.adIntegrationService.SaveGroupMappings(saveData)).then(() => {
            this.changes = [];
            this.snackBar.showSnackbar({
                textMessage: this.appTextService.get('ad_integration.mapping_saved'),
                buttonText: this.appTextService.get('ok'),
            });
            this.setDirtyCallback(false);
        });
    }

    loadData() {
        this.adIntegrationService.GetGroupMappings().then((data: IADGroupMapping[]) => {
            // todo add many to many relationship support
            const listItems: IADGroupMappingListModel[] = data.map((dataItem: any) => {
                return Object.assign(
                    {},
                    dataItem,
                    { Mappings: undefined },
                    dataItem.Mappings.length ? dataItem.Mappings[0] : {},
                );
            });
            this.setLoadedData(listItems);
        });
    }

    setLoadedData(loadedData) {
        this.loadedData = loadedData;
        this.loadedData.forEach((item) => (item.ParentGroups = []));
        this.loadedData.forEach((group) => {
            group.ChildGroups = this.loadedData.filter((item) => item.GroupParentGuids.indexOf(group.GroupGuid) !== -1);
            group.ChildGroups.forEach((item) => item.ParentGroups.push(group));
            group.IsContainer =
                group.GroupCategory === ADGroupMappingGroupCategory.OrganizationUnit || group.ChildGroups.length > 0;
        });
        this.updateDisplayData();
    }

    updateDisplayData() {
        const dataCopy = buildDisplayDataItems(this.loadedData);
        const filteredItems = filterSelected(dataCopy, this.filter);
        const rootItems = filteredItems.filter((item) => !item.group.GroupParentGuids.length);
        const notEmptyData = filterEmptyContainers(rootItems);
        this.displayData = sortLoadedData(notEmptyData);
    }

    onChanges($event) {
        this.addChanges($event);
        this.updateDisplayData();
        this.setDirtyCallback(true);
    }

    addChanges(changes: IADGroupMappingListModel[]) {
        changes.forEach((item) => {
            if (this.changes.indexOf(item) === -1) {
                this.changes.push(item);
            }
        });
    }
}

function buildDisplayDataItems(data: any[]): IADDisplayDataItem[] {
    return data.map((group) => ({
        group: group,
        ChildGroups: buildDisplayDataItems(group.ChildGroups),
    }));
}

function filterEmptyContainers(data: IADDisplayDataItem[]) {
    return data.filter((dataItem) => {
        if (!dataItem.group.IsContainer) {
            return true;
        }

        dataItem.ChildGroups = filterEmptyContainers(dataItem.ChildGroups);
        return dataItem.ChildGroups.length > 0;
    });
}

function filterSelected(data: IADDisplayDataItem[], sideOption) {
    if (!sideOption.filter) {
        return data.slice();
    }

    return data.filter((item) => {
        if (item.group.IsContainer) {
            item.ChildGroups = filterSelected(item.ChildGroups, sideOption);
            return true;
        } else {
            return item.group.ObjectType == sideOption.ObjectType;
        }
    });
}

function sortLoadedData(loadedData: IADDisplayDataItem[]) {
    loadedData.forEach((item) => {
        item.ChildGroups = sortLoadedData(item.ChildGroups);
    });

    return loadedData.sort((ai, bi) => {
        const a = ai.group,
            b = bi.group;

        if (a.IsContainer && !b.IsContainer) {
            return -1;
        } else if (!a.IsContainer && b.IsContainer) {
            return 1;
        } else if (a.GroupName > b.GroupName) {
            return 1;
        } else if (a.GroupName < b.GroupName) {
            return -1;
        } else {
            return 0;
        }
    });
}
